import { INoteRef, IPreparedNote, SpecialNotes } from "@fokused-me/core";
import {
   ContentModeType,
   ContentPanelActionType,
   FMAppContentPanel,
   FMPanelNote,
   INodePanelParams,
   INoteTreeAction,
   INoteTreeNodeMove,
   LazyNoteTree,
   markdownRenderer,
   NoteTreeNodeButtonType,
} from "@fokused-me/web-ui";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { List, Map, OrderedMap, Set } from "immutable";
import { useCallback, useMemo, useRef } from "react";
import { createPanelInfo, useDesktop } from "../../../state/Desktop";
import { ITreePanelInfo } from "../../../state/Model";
import { useNotepads } from "../../../state/Notepads";
import { IContentPanelProps } from "./ContentPanelSelector";
import {
   newNotePanel,
   processNoteTreeNodeMoveEvent,
   useBreadcrumbs,
   useDeleteNoteWithConfirmation,
   usePanelNodeDropped,
} from "./Helpers";

export interface ITreeContentAreaProps extends IContentPanelProps {
   panelInfo: ITreePanelInfo;
}

export const TreeContentPanel = ({ notepad, panelInfo, notepadState }: ITreeContentAreaProps) => {
   const theme = useTheme();
   const compact = useMediaQuery(theme.breakpoints.down("sm"));
   const { notepads } = useNotepads();
   const { desktopInfo, toggleNoteSelection, toggleSortMode } = useDesktop();

   const rootElRef = useRef<HTMLDivElement>();

   const {
      setPaneRootNoteId,
      toggleExpandedNote,
      toggleExpandedText,
      toggleExpandPanel,
      addPanel,
      closePanels,
      setPanelType,
   } = useDesktop();

   const { ids, levels } = useMemo(
      () =>
         notepad
            ? notepad.query({
                 rootId: panelInfo.rootNoteId,
                 expanded: panelInfo.expandedNote,
                 includeRoot: panelInfo.rootNoteId !== SpecialNotes.HOME,
                 sort: panelInfo.sortMode,
              })
            : { ids: List<string>(), levels: Map<string, number>() },
      /* eslint-disable-next-line */
      [notepad, panelInfo.rootNoteId, panelInfo.expandedNote, panelInfo.sortMode, notepadState]
   );

   const handleSetPanelRootNoteId = useCallback(
      (itemId: string) => {
         setPaneRootNoteId(panelInfo.id, itemId);
      },
      [panelInfo.id, setPaneRootNoteId]
   );

   const breadcrumbs = useBreadcrumbs(panelInfo, notepad);

   const handleModeChange = useCallback(
      (mode: ContentModeType) => setPanelType(panelInfo.id, mode),
      [panelInfo.id, setPanelType]
   );

   const handlePanelAction = useCallback(
      (action: ContentPanelActionType) => {
         switch (action) {
            case "expand":
               toggleExpandPanel(panelInfo.id);
               break;
            case "close":
               closePanels(List([panelInfo.id]));
               break;
            case "add":
               newNotePanel(panelInfo, addPanel);
               break;
            case "sort-time":
               toggleSortMode(panelInfo.id, "time");
               break;
            case "sort-title":
               toggleSortMode(panelInfo.id, "title");
               break;
         }
      },
      [panelInfo, closePanels, addPanel, toggleExpandPanel, toggleSortMode]
   );

   const { confirmDeleteDialog, handleDeleteNotes } = useDeleteNoteWithConfirmation(notepads);

   const handleNoteTreeAction = useCallback(
      (action: INoteTreeAction) => {
         switch (action.type) {
            case "toggleExpand":
               toggleExpandedNote(panelInfo.id, action.itemId);
               break;
            case "toggleExpandText":
               toggleExpandedText(panelInfo.id, action.itemId);
               break;
            case "navigate":
               handleSetPanelRootNoteId(action.itemId);
               break;
            case "open": {
               const note = notepad!.getNote(action.itemId)!;
               const panel = createPanelInfo({
                  notepadId: panelInfo.notepadId,
                  rootNoteId: action.itemId,
                  type: note.childrenCount ? "tree" : "note",
               });
               addPanel(panel);
               break;
            }
            case "edit": {
               const panel = createPanelInfo({
                  notepadId: panelInfo.notepadId,
                  rootNoteId: action.itemId,
                  type: "edit",
               });
               addPanel(panel);
               break;
            }
            case "create":
               newNotePanel({ ...panelInfo, rootNoteId: action.itemId }, addPanel);
               break;
            case "select":
               toggleNoteSelection({ noteId: action.itemId, notepadId: panelInfo.notepadId });
               break;
            case "delete":
               handleDeleteNotes(List<INoteRef>().push({ noteId: action.itemId, notepadId: panelInfo.notepadId }));
               break;
         }
      },
      [
         notepad,
         panelInfo,
         toggleExpandedNote,
         toggleExpandedText,
         addPanel,
         handleSetPanelRootNoteId,
         toggleNoteSelection,
         handleDeleteNotes,
      ]
   );

   const handlePrepareNodePanel = useCallback(
      (id: string, params: INodePanelParams) => {
         if (compact) {
            params.leftPosition = undefined;
            params.rightPosition = 70;
         } else {
            const x0 = (window.innerWidth - (rootElRef.current ? rootElRef.current.clientWidth : 0)) / 2;
            params.leftPosition = parseInt((params.leftPosition || 0).toString(), 10) + x0;
         }

         params.buttonsShow = Set([
            "select",
            "delete",
            "create",
            "edit",
            "navigate",
            "open",
         ]) as Set<NoteTreeNodeButtonType>;
         return params;
      },
      [compact]
   );

   const handleHasItemChanged = useCallback(
      (item: IPreparedNote, component: any, hasChanged: boolean) => {
         if (hasChanged) {
            return hasChanged;
         } else {
            return (levels.get(item.id) || 1) - 1 !== component.props.pads;
         }
      },
      [levels]
   );

   const handleTreeNodeMove = useCallback(
      (event: INoteTreeNodeMove) => {
         processNoteTreeNodeMoveEvent(panelInfo.notepadId, notepads, event, false);
      },
      [notepads, panelInfo.notepadId]
   );

   const handlePanelNodeDropped = usePanelNodeDropped(panelInfo.notepadId);

   const actions = useMemo(
      () => Set.of<ContentPanelActionType>("add", "settings", "sort-title", "sort-time", "search"),
      []
   );

   const selectedMap = useMemo(() => {
      let map = OrderedMap<string, string>();
      desktopInfo.selectedNotes.forEach((ref) => (map = map.set(ref.noteId, ref.noteId)));
      return map;
   }, [desktopInfo.selectedNotes]);

   const disableModes = useMemo(() => {
      let result = Set<ContentModeType>();
      if (panelInfo.rootNoteId === SpecialNotes.HOME) {
         result = result.add("note").add("edit");
      }
      return result;
   }, [panelInfo.rootNoteId]);

   const highlightedActions = useMemo(
      () =>
         panelInfo.sortMode === "time"
            ? Set.of<ContentPanelActionType>("sort-time")
            : panelInfo.sortMode === "title"
            ? Set.of<ContentPanelActionType>("sort-title")
            : Set(),
      [panelInfo.sortMode]
   );

   return (
      <FMAppContentPanel
         expanded={panelInfo.expanded}
         actions={actions}
         highlightedActions={highlightedActions}
         disableActions={Set()}
         disableModes={disableModes}
         mode={panelInfo.type}
         breadcrumbs={breadcrumbs}
         onAction={handlePanelAction}
         onModeChange={handleModeChange}
         onBreadcrumbsNavigate={console.log}
         onSearchTextChange={console.log}
      >
         {ids.size ? (
            <LazyNoteTree
               notepadId={notepad?.getId() || ""}
               loadIncrement={10}
               disableDragOrder={!!panelInfo.sortMode}
               onAction={handleNoteTreeAction}
               onNodeMove={handleTreeNodeMove}
               onTextDropped={console.log}
               // onScrollPosition={setTreeViewScrollPos}
               expanded={panelInfo.expandedNote}
               textExpanded={panelInfo.expandedText}
               // initialScrollPosition={
               //    (viewSettings && viewSettings.scrollPos) || 0
               // }
               // fontSize={navigatorFontSize}
               hasItemChanged={handleHasItemChanged}
               notes={ids.map((id) => notepad!.getNote(id)!)}
               levels={levels}
               rootRef={rootElRef}
               selected={selectedMap}
               prepareText={prepareText}
               prepareNodePanel={handlePrepareNodePanel}
            />
         ) : (
            <FMPanelNote
               text=""
               notepadId={panelInfo.notepadId}
               noteId={panelInfo.rootNoteId}
               onNodeDropped={handlePanelNodeDropped}
            />
         )}
         {confirmDeleteDialog}
      </FMAppContentPanel>
   );
};

function prepareText(note: IPreparedNote) {
   // const textExpanded = this.props.viewSettings.textExpanded;
   const text = note.text;

   return text ? markdownRenderer.render(text) : "";
}
